import type { User, UserManagerSettings } from 'oidc-client-ts';
import {
  extractOidcPermissions,
  extractOidcRealmRoles,
  extractOidcRoles,
  type OidcClaimsLike,
} from '@nwm-oss/auth-core/oidc';

export interface FrontendOidcIdentityOptions {
  readonly clientId?: string;
}

export function buildFrontendIdentityFromOidcUser(
  user: User,
  options: FrontendOidcIdentityOptions = {},
) {
  const claims = user.profile as OidcClaimsLike;
  const roles = extractOidcRoles(claims, options.clientId);

  return {
    subjectId: claims.sub ?? user.profile.sub ?? 'unknown',
    displayName: claims.name,
    email: claims.email,
    username: claims.preferred_username,
    organizationId: claims.organization_id,
    // In standalone OIDC mode the JWT org == the active org (no dynamic switching).
    activeOrgId: claims.organization_id,
    roles,
    realmRoles: extractOidcRealmRoles(claims),
    permissions: extractOidcPermissions(claims, options.clientId),
  };
}

export interface CreateOidcSettingsInput {
  readonly authority: string;
  readonly clientId: string;
  readonly audience?: string;
  readonly redirectUri: string;
  readonly postLogoutRedirectUri: string;
  readonly scope?: string;
}

export function createOidcSettings(input: CreateOidcSettingsInput): UserManagerSettings {
  return {
    authority: input.authority,
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    post_logout_redirect_uri: input.postLogoutRedirectUri,
    scope: input.scope ?? 'openid profile email',
    response_type: 'code',
    extraQueryParams: input.audience ? { audience: input.audience } : undefined,
    automaticSilentRenew: true,
    loadUserInfo: true,
    filterProtocolClaims: true,
  };
}
